import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
} from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { NotifierService } from 'angular-notifier';
import { BoobleModalService } from 'booble-modal';
import {
  Observable,
  ReplaySubject,
  Subject,
  firstValueFrom,
  map,
  take,
  takeUntil,
} from 'rxjs';
import { Moedas } from 'src/app/domain/constants/moeda.constant';
import { CountryDto } from 'src/app/domain/models/dto/country.dto';
import { CreateOrderAdminDto } from 'src/app/domain/models/dto/create-order-admin.dto';
import { GetAllCnpjDto } from 'src/app/domain/models/dto/get-all-cnpj.dto';
import { GetAllCpfDto } from 'src/app/domain/models/dto/get-all-cpf.dto';
import { sanitizeCnpj } from 'src/app/shared/utils/cnpj.util';
import { cnpjValidator } from 'src/app/shared/validators/form-validators';
import { BankAccountService } from 'src/app/shared/services/bank-account.service';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { CountriesService } from 'src/app/shared/services/countries.service';
import { IndividualAccountService } from 'src/app/shared/services/individual-account.service';
import { OrdersService } from 'src/app/shared/services/orders.service';

@Component({
  selector: 'app-create-inbound-order',
  templateUrl: './create-inbound-order.component.html',
  styleUrls: ['./create-inbound-order.component.scss'],
})
export class CreateInboundOrderComponent implements OnInit {
  currency: string = '';
  moedas = Moedas;

  formInfo!: FormGroup;
  startDate = new Date();
  deme$: Observable<string[]>;

  private readonly notifier: NotifierService;
  public loading: boolean;
  personType: 'PJ' | 'PF' = 'PJ';
  companyList: GetAllCnpjDto[] = [];
  personList: GetAllCpfDto[] = [];
  countries$: Observable<CountryDto[]>;
  corporateAccounts$: Observable<GetAllCnpjDto[]>;
  public companyListFilterCtrl = new FormControl('');
  public personListFilterCtrl = new FormControl('');
  public filteredCompanyList: ReplaySubject<GetAllCnpjDto[]> =
    new ReplaySubject<GetAllCnpjDto[]>(1);
  public filteredPersonList: ReplaySubject<GetAllCpfDto[]> = new ReplaySubject<
    GetAllCpfDto[]
  >(1);

  @ViewChild('singleSelect', { static: true }) singleSelect: MatSelect;
  @ViewChild('cpfSelect', { static: true }) cpfSelect: MatSelect;

  protected _onDestroy = new Subject<void>();

  constructor(
    private modalService: BoobleModalService,
    private dateAdapter: DateAdapter<any>,
    private readonly countriesService: CountriesService,
    formBuilder: FormBuilder,
    private readonly bankService: BankAccountService,
    private readonly companyService: CorporateAccountService,
    private readonly individualAccountService: IndividualAccountService,
    private readonly ordersService: OrdersService,
    notifierService: NotifierService
  ) {
    this.countries$ = this.countriesService.get().pipe(map((res) => res.data));

    this.setLocale();
    this.notifier = notifierService;

    this.formInfo = formBuilder.group({
      cnpj: [''],
      cpf: [''],
      companyName: [''],
      businessPartnerName: [''],
      name: [''],
      currency: [
        {
          id: 1,
          value: 'USD',
          description: 'USD',
          image: 'assets/flags/usd.svg',
          icon: 'usd',
        },
        Validators.required,
      ],
      amountReceived: ['', [Validators.required, Validators.min(1)]],
      nameSender: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      countrySenderId: ['', Validators.required],
      paymentDate: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      bankName: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      orderNumber: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
    });

    this.formInfoControls.currency.valueChanges.subscribe((currencyForm) => {
      this.currency = `${currencyForm.value} `;

      this.deme$ = this.bankService.getDemeBanks(this.currency)
        .pipe(
          map(v => v.data
            .filter(x => x.remmitanceTypes
              .includes('INBOUND'))
                .map(x => x.bankName))
        );
    });
  }

  get formInfoControls() {
    return this.formInfo.controls;
  }

  get info(): CreateOrderAdminDto {
    const info = this.formInfo.value;
    return info;
  }

  ngOnInit() {
    this.formInfo.controls['businessPartnerName'].disable({ emitEvent: false });
    this.formInfo.controls['companyName'].disable({ emitEvent: false });
    this.formInfo.controls['name'].disable({ emitEvent: false });

    this.applyPersonTypeRules(this.personType);

    firstValueFrom(this.companyService.getAllCnpj()).then((value) => {
      this.companyList = value.data;

      this.filteredCompanyList.next(this.companyList);
    });

    firstValueFrom(this.individualAccountService.getAllCpf()).then((value) => {
      this.personList = value.data;

      this.filteredPersonList.next(this.personList);
    });

    this.companyListFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterCompanyList();
      });

    this.personListFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterPersonList();
      });
  }

  onPersonTypeChange(type: 'PJ' | 'PF') {
    this.personType = type;
    this.applyPersonTypeRules(type);
  }

  private applyPersonTypeRules(type: 'PJ' | 'PF') {
    const { cnpj, companyName, cpf, name, businessPartnerName } =
      this.formInfoControls;

    businessPartnerName.reset('', { emitEvent: false });
    businessPartnerName.disable({ emitEvent: false });

    if (type === 'PJ') {
      cnpj.enable({ emitEvent: false });
      cnpj.setValidators([Validators.required, cnpjValidator]);

      cpf.reset('', { emitEvent: false });
      cpf.clearValidators();
      cpf.disable({ emitEvent: false });

      name.reset('', { emitEvent: false });
      companyName.clearValidators();
      name.clearValidators();
      companyName.disable({ emitEvent: false });
      name.disable({ emitEvent: false });
    } else {
      cpf.enable({ emitEvent: false });
      cpf.setValidators([Validators.required]);

      cnpj.reset('', { emitEvent: false });
      cnpj.clearValidators();
      cnpj.disable({ emitEvent: false });

      companyName.reset('', { emitEvent: false });
      companyName.clearValidators();
      name.clearValidators();
      companyName.disable({ emitEvent: false });
      name.disable({ emitEvent: false });
    }

    cnpj.updateValueAndValidity({ emitEvent: false });
    companyName.updateValueAndValidity({ emitEvent: false });
    cpf.updateValueAndValidity({ emitEvent: false });
    name.updateValueAndValidity({ emitEvent: false });
    businessPartnerName.updateValueAndValidity({ emitEvent: false });
  }

  changeCompany(event: any) {
    this.formInfo.controls['cnpj'].setValue(event.value);
    const company = this.companyList.find((i) => i.cnpj === event.value);
    this.formInfo.controls['companyName'].setValue(company?.companyName ?? '');
    this.formInfo.controls['businessPartnerName'].setValue(
      company?.businessPartnerName ?? ''
    );
    this.formInfo.updateValueAndValidity({ emitEvent: false });
  }

  changePerson(event: any) {
    this.formInfo.controls['cpf'].setValue(event.value);
    const person = this.personList.find((i) => i.cpf === event.value);
    this.formInfo.controls['name'].setValue(person?.name ?? '');
    this.formInfo.controls['businessPartnerName'].setValue(
      person?.businessPartnerName ?? ''
    );
    this.formInfo.updateValueAndValidity({ emitEvent: false });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected setInitialValue() {
    this.filteredCompanyList
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.singleSelect.compareWith = (a: GetAllCnpjDto, b: GetAllCnpjDto) =>
          a && b && a.id === b.id;
      });

    this.filteredPersonList
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.cpfSelect.compareWith = (a: GetAllCpfDto, b: GetAllCpfDto) =>
          a && b && a.id === b.id;
      });
  }

  protected filterCompanyList() {
    if (!this.companyList) return;

    const search = this.companyListFilterCtrl.value ?? '';
    const searchClean = sanitizeCnpj(search);

    if (!searchClean && !search.trim()) {
      this.filteredCompanyList.next(this.companyList);
      return;
    }

    this.filteredCompanyList.next(
      this.companyList.filter((company) => {
        const cnpjClean = sanitizeCnpj(company.cnpj);
        return (
          cnpjClean.includes(searchClean) ||
          company.cnpj.toLowerCase().includes(search.toLowerCase())
        );
      })
    );
  }

  protected filterPersonList() {
    if (!this.personList) return;

    const search = this.personListFilterCtrl.value ?? '';
    const searchDigits = search.replace(/\D/g, '');

    if (!searchDigits && !search.trim()) {
      this.filteredPersonList.next(this.personList);
      return;
    }

    this.filteredPersonList.next(
      this.personList.filter((person) => {
        const cpfDigits = person.cpf.replace(/\D/g, '');
        return (
          cpfDigits.includes(searchDigits) ||
          person.cpf.toLowerCase().includes(search.toLowerCase())
        );
      })
    );
  }

  closeModal() {
    this.modalService.dismiss(null);
  }

  setLocale() {
    this.dateAdapter.setLocale('pt-BR');
  }

  private buildPayload(): CreateOrderAdminDto {
    const v = this.formInfo.getRawValue();

    const common: Omit<
      CreateOrderAdminDto,
      'document' | 'companyName' | 'name'
    > = {
      currency: v.currency,
      amountReceived: v.amountReceived,
      nameSender: v.nameSender,
      countrySenderId: v.countrySenderId,
      paymentDate: v.paymentDate,
      bankName: v.bankName,
      businessPartnerName: v.businessPartnerName,
      orderNumber: v.orderNumber,
    };

    return this.personType === 'PJ'
      ? { ...common, document: v.cnpj, companyName: v.companyName }
      : { ...common, document: v.cpf, name: v.name };
  }

  sendConfirm() {
    if (this.formInfo.invalid) {
      this.formInfo.markAllAsTouched();
      return;
    }

    this.loading = true;
    const payload = this.buildPayload();

    this.ordersService.createOrderInboundAdmin(payload).subscribe({
      next: () => {
        this.loading = false;
        this.closeModal();
        this.notifier.notify(
          'success',
          'Ordem criada com sucesso e em instantes o cliente será avisado sobre o recebimento.'
        );
        this.ordersService.updateOrderView();
      },
      error: () => (this.loading = false),
    });
  }
}
